import { numericLocaleCompare, orderByComparer, scheduledIntervalComparer } from '@app/shared/utils/sortable-util';
import {
  assignmentComparer,
  batchReportComparer,
  BillableItem,
  CognitoUser,
  Customer,
  DeliverySlip,
  Employee,
  invoiceComparer,
  IotGateway,
  JobState,
  LiveBatch,
  mixDesignComparer,
  mixDesignIngredientComparer,
  PlcBatchRecord,
  PriceLevel,
  projectComparer, TenantUser,
  truckComparer,
  Unit,
} from '@cco/model';
import { EntityDataModuleConfig } from '@ngrx/data';
import { EntityUrlMap } from '@app/data/cco-entity-url-registry.service';
import { CcoEntityMetadataMap } from '@app/data/entity-metadata';
import { Campaign, TenantCampaignRegistration, TenantIdentityAuthorization } from '@cco/mailer';

export interface ProvinceAddlState {
  loadedByCountryIdAt: {
    [countryId: number]: string;
  };
}

const DEFAULT_PROPERTIES = {
  additionalCollectionState: {
    loadedAt: null,
    queryLoadedAt: {},
    lastEvaluatedKeyMap: {},
  },
  entityDispatcherOptions: {
    optimisticDelete: false,
  },
  noChangeTracking: true,
};

const entityMetadata: CcoEntityMetadataMap = {
  AccountingUnit: {
    ...DEFAULT_PROPERTIES,
  },
  Address: {
    ...DEFAULT_PROPERTIES,
  },
  Assignment: {
    ...DEFAULT_PROPERTIES,
    paged: true,
    timeBased: true,
    sortComparer: assignmentComparer,
  },
  AssignmentState: {
    ...DEFAULT_PROPERTIES,
  },
  AssignmentStatus: {
    ...DEFAULT_PROPERTIES,
    sortComparer: orderByComparer,
  },
  AssignmentTarget: {
    ...DEFAULT_PROPERTIES,
  },
  AssignmentTargetType: {
    ...DEFAULT_PROPERTIES,
  },
  AssignmentTargetExternalCalendarAssociation: {
    ...DEFAULT_PROPERTIES,
  },
  AssignmentType: {
    ...DEFAULT_PROPERTIES,
    sortComparer: orderByComparer,
  },
  BatchReport: {
    ...DEFAULT_PROPERTIES,
    paged: true,
    timeBased: true,
    sortComparer: batchReportComparer,
  },
  BillableItem: {
    ...DEFAULT_PROPERTIES,
    sortComparer: (a: BillableItem, b: BillableItem) => {
      const aSku = a.sku;
      const bSku = b.sku;
      return aSku.localeCompare(bSku);
    }
  },
  BillableItemAssociation: {
    ...DEFAULT_PROPERTIES,
    sortComparer: orderByComparer,
  },
  BillableItemAssociationType: {
    ...DEFAULT_PROPERTIES,
    sortComparer: orderByComparer,
  },
  BillableItemQuantityDeterminer: {
    ...DEFAULT_PROPERTIES,
    sortComparer: orderByComparer,
  },
  Company: {
    ...DEFAULT_PROPERTIES,
  },
  Contact: {
    additionalCollectionState: {
      loadedAt: null,
      queryLoadedAt: {},
      paged: true,
    },
    paged: true,
  },
  Country: {
    ...DEFAULT_PROPERTIES,
  },
  Customer: {
    additionalCollectionState: {
      paged: true,
      loadedAt: null,
      queryLoadedAt: {},
    },
    paged: true,
    sortComparer: (a: Customer, b: Customer) => a?.name?.localeCompare(b?.name),
  },
  DeliverySlip: {
    additionalCollectionState: {
      loadedAt: null,
      queryLoadedAt: {},
      paged: true,
    },
    noChangeTracking: true,
    paged: true,
    timeBased: true,
    sortComparer: (a: DeliverySlip, b: DeliverySlip) =>
      -numericLocaleCompare(a?.documentNumber, b?.documentNumber),
  },
  EmailCampaign: {
    ...DEFAULT_PROPERTIES,
    selectId: (campaign: Campaign) => campaign.campaignCode,
    sortComparer: (a: Campaign, b: Campaign) => a.name.localeCompare(b.campaignCode),
  },
  Employee: {
    ...DEFAULT_PROPERTIES,
    sortComparer: (a: Employee, b: Employee) => {
      const aGivenNames = a.givenNames ?? '';
      const aSurname = a.surname ?? '';
      return aGivenNames.localeCompare(b.givenNames ?? '') || aSurname.localeCompare(b.surname ?? '');
    }
  },
  ExposureClass: {
    ...DEFAULT_PROPERTIES,
  },
  GoogleCalendar: {
    ...DEFAULT_PROPERTIES,
  },
  IotGateway: {
    ...DEFAULT_PROPERTIES,
    selectId: (iotGateway: IotGateway) => iotGateway?.thingName,
  },
  InvoiceTerm: {
    ...DEFAULT_PROPERTIES,
  },
  Invoice: {
    ...DEFAULT_PROPERTIES,
    paged: true,
    timeBased: true,
    sortComparer: invoiceComparer,
  },
  Job: {
    ...DEFAULT_PROPERTIES,
    paged: true,
    timeBased: true,
    sortComparer: scheduledIntervalComparer,
  },
  JobRelatedEntitiesAndStats: {
    ...DEFAULT_PROPERTIES,
  },
  JobState: {
    ...DEFAULT_PROPERTIES,
    sortComparer: (a: JobState, b: JobState) => a?.id - b?.id,
  },
  LiveBatch: {
    ...DEFAULT_PROPERTIES,
    selectId: (liveBatch: LiveBatch) => liveBatch.assetUuid,
  },
  MixDesign: {
    ...DEFAULT_PROPERTIES,
    sortComparer: mixDesignComparer,
  },
  MixDesignDecisionTreeNode: {
    ...DEFAULT_PROPERTIES,
  },
  MixDesignIngredient: {
    ...DEFAULT_PROPERTIES,
    sortComparer: mixDesignIngredientComparer,
  },
  MixDesignIngredientType: {
    ...DEFAULT_PROPERTIES,
    sortComparer: orderByComparer,
  },
  PlcBatchRecord: {
    ...DEFAULT_PROPERTIES,
    selectId: (plcBatchRecord: PlcBatchRecord) =>
      `${plcBatchRecord.assetUuid}#${plcBatchRecord.batchNumber}`,
    sortComparer: (a: PlcBatchRecord, b: PlcBatchRecord) => b.batchNumber - a.batchNumber,
  },
  PriceLevel: {
    ...DEFAULT_PROPERTIES,
    sortComparer: (a: PriceLevel, b: PriceLevel) => a?.name && b?.name && a.name.localeCompare(b.name),
  },
  Privilege: {
    ...DEFAULT_PROPERTIES,
  },
  Project: {
    ...DEFAULT_PROPERTIES,
    timeBased: true,
    sortComparer: projectComparer,
  },
  ProjectStats: {
    ...DEFAULT_PROPERTIES,
  },
  Province: {
    ...DEFAULT_PROPERTIES,
  },
  RegisteredEmailCampaign: {
    ...DEFAULT_PROPERTIES,
    selectId: (registration: TenantCampaignRegistration) => registration.campaignCode,
  },
  Role: {
    ...DEFAULT_PROPERTIES,
  },
  Sage50UsTax: {
    ...DEFAULT_PROPERTIES,
  },
  Sage50UsBillableItem: {
    ...DEFAULT_PROPERTIES,
  },
  SystemTagDimension: {
    ...DEFAULT_PROPERTIES,
  },
  Tax: {
    ...DEFAULT_PROPERTIES,
  },
  TaxGroup: {
    ...DEFAULT_PROPERTIES,
  },
  Tag: {
    ...DEFAULT_PROPERTIES,
  },
  TenantUser: {
    ...DEFAULT_PROPERTIES,
    sortComparer: (a: TenantUser, b: TenantUser) => {
      const comp = a?.givenNames && b?.givenNames && a.givenNames.localeCompare(b.givenNames);
      if (comp !== 0)
        return comp;
      return a?.surname && b?.surname && a.surname.localeCompare(b.surname);
    },
  },
  Truck: {
    ...DEFAULT_PROPERTIES,
    sortComparer: truckComparer,
  },
  Unit: {
    ...DEFAULT_PROPERTIES,
    sortComparer: (a: Unit, b: Unit) => a?.shortName && b?.shortName && a.shortName?.localeCompare(b.shortName),
  },
  User: {
    ...DEFAULT_PROPERTIES,
    selectId: (u: CognitoUser) => u?.sub,
    sortComparer: (a: CognitoUser, b: CognitoUser) => {
      const comp = a?.givenNames && b?.givenNames && a.givenNames.localeCompare(b.givenNames);
      if (comp !== 0)
        return comp;
      return a?.surname && b?.surname && a.surname.localeCompare(b.surname);
    },
  },
  VerifiedEmailIdentity: {
    ...DEFAULT_PROPERTIES,
    selectId: (identity: TenantIdentityAuthorization) => identity.identityName,
    sortComparer: (a: TenantIdentityAuthorization, b: TenantIdentityAuthorization) => {
      if (a.identityType !== b.identityType)
        return a.identityType.localeCompare(b.identityType);

      return a.identityName.localeCompare(b.identityName);
    },
  },
};

const pluralNames = {
  Address: 'Addresses',
  AssignmentStatus: 'AssignmentStatuses',
  Company: 'Companies',
  Country: 'Countries',
  ExposureClass: 'ExposureClasses',
  Tax: 'Taxes',
  Sage50UsTax: 'Sage50UsTaxes',
};

export const entityConfig: EntityDataModuleConfig = {
  entityMetadata,
  pluralNames,
};

export const entityUrlMap: EntityUrlMap = {
  BillableItemAssociation: {
    entityResourceUrl: 'billable-items/associations',
    collectionResourceUrl: 'billable-items/associations',
  },
  BillableItemAssociationType: {
    entityResourceUrl: 'billable-items/association-types',
    collectionResourceUrl: 'billable-items/association-types',
  },
  BillableItemQuantityDeterminer: {
    entityResourceUrl: 'billable-items/quantity-determiners',
    collectionResourceUrl: 'billable-items/quantity-determiners',
  },
  EmailCampaign: {
    entityResourceUrl: 'campaigns',
    collectionResourceUrl: 'campaigns',
  },
  GoogleCalendar: {
    collectionResourceUrl: 'integration/google-api/calendars',
    entityResourceUrl: 'integration/google-api/calendars',
  },
  JobRelatedEntitiesAndStats: {
    entityResourceUrl: 'jobs/related-entities-and-stats',
    collectionResourceUrl: 'jobs/related-entities-and-stats',
  },
  LiveBatch: {
    entityResourceUrl: 'live-batch',
    collectionResourceUrl: 'live-batch',
  },
  MixDesignDecisionTreeNode: {
    entityResourceUrl: 'mix-design-decision-tree',
    collectionResourceUrl: 'mix-design-decision-tree',
  },
  ProjectStats: {
    entityResourceUrl: 'projects/stats',
    collectionResourceUrl: 'projects/stats',
  },
  RegisteredEmailCampaign: {
    entityResourceUrl: 'registered-campaigns',
    collectionResourceUrl: 'registered-campaigns',
  },
  SystemTagDimension: {
    entityResourceUrl: 'system-tags/dimensions',
    collectionResourceUrl: 'system-tags/dimensions',
  },
  Sage50UsTax: {
    entityResourceUrl: 'external/accounting/sage50us/sage50Us-taxes',
    collectionResourceUrl: 'external/accounting/sage50us/sage50Us-taxes',
  },
  Sage50UsBillableItem: {
    entityResourceUrl: 'external/accounting/sage50us/sage50Us-billable-items',
    collectionResourceUrl: 'external/accounting/sage50us/sage50Us-billable-items',
  },
  VerifiedEmailIdentity: {
    entityResourceUrl: 'identities',
    collectionResourceUrl: 'identities',
  },
};
